import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, Lightbulb, CheckCircle, ChevronRight, Play, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '../components/Card';
interface MelodyModuleProps {
  onBack: () => void;
}
// Audio helper
function playNote(freq: number, duration: number = 0.4) {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const osc = audioContext.createOscillator();
  const gain = audioContext.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, audioContext.currentTime);
  osc.connect(gain);
  gain.connect(audioContext.destination);
  gain.gain.setValueAtTime(0.3, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  osc.start(audioContext.currentTime);
  osc.stop(audioContext.currentTime + duration);
}
function playMelody(notes: number[], interval: number = 300) {
  notes.forEach((freq, i) => {
    setTimeout(() => playNote(freq, 0.35), i * interval);
  });
}
export function MelodyModule({
  onBack
}: MelodyModuleProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [score, setScore] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [drawnPath, setDrawnPath] = useState<{
    x: number;
    y: number;
  }[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const lessonSteps = [{
    type: 'learn',
    title: '什么是旋律？',
    icon: '🎼',
    content: {
      definition: '旋律是音乐的"灵魂"，是由一连串高低不同的音按照一定的节奏组合而成的音乐线条。',
      keyPoints: ['旋律就像音乐的"故事线"，有起伏变化', '旋律由音高的上升、下降和保持组成', '好听的旋律让人想跟着哼唱'],
      example: '想象一下：过山车的轨道有高有低，旋律就像声音的"过山车"！',
      visual: '📈 上升 → 📉 下降 → ➡️ 平稳'
    }
  }, {
    type: 'interactive',
    title: '旋律的三种走向',
    icon: '📊',
    content: {
      directions: [{
        name: '上行旋律',
        icon: '📈',
        desc: '音从低到高，像爬楼梯',
        notes: [261.63, 293.66, 329.63, 349.23, 392],
        color: 'bg-green-100 text-green-700 border-green-200'
      }, {
        name: '下行旋律',
        icon: '📉',
        desc: '音从高到低，像滑滑梯',
        notes: [523.25, 493.88, 440, 392, 349.23],
        color: 'bg-blue-100 text-blue-700 border-blue-200'
      }, {
        name: '平行旋律',
        icon: '➡️',
        desc: '音高相同，像走平路',
        notes: [392, 392, 392, 392, 392],
        color: 'bg-purple-100 text-purple-700 border-purple-200'
      }]
    }
  }, {
    type: 'quiz',
    title: '听音辨走向',
    icon: '👂',
    content: {
      question: '听一段旋律，判断它是上行、下行还是平行？',
      melodies: [{
        notes: [261.63, 329.63, 392, 523.25],
        answer: 0,
        name: '上行'
      }, {
        notes: [523.25, 440, 349.23, 261.63],
        answer: 1,
        name: '下行'
      }, {
        notes: [392, 392, 392, 392],
        answer: 2,
        name: '平行'
      }],
      currentMelody: 0
    }
  }, {
    type: 'draw',
    title: '画出旋律线',
    icon: '✏️',
    content: {
      instruction: '听一段旋律，用手指画出它的走向！',
      targetMelody: [261.63, 329.63, 392, 329.63, 261.63] // Up then down
    }
  }, {
    type: 'create',
    title: '创作你的旋律',
    icon: '🎨',
    content: {
      instruction: '点击音符，创作一段属于你的旋律！',
      notes: [{
        name: 'Do',
        freq: 261.63,
        color: 'bg-red-500'
      }, {
        name: 'Re',
        freq: 293.66,
        color: 'bg-orange-500'
      }, {
        name: 'Mi',
        freq: 329.63,
        color: 'bg-yellow-500'
      }, {
        name: 'Fa',
        freq: 349.23,
        color: 'bg-green-500'
      }, {
        name: 'Sol',
        freq: 392,
        color: 'bg-teal-500'
      }, {
        name: 'La',
        freq: 440,
        color: 'bg-blue-500'
      }, {
        name: 'Si',
        freq: 493.88,
        color: 'bg-purple-500'
      }, {
        name: 'Do',
        freq: 523.25,
        color: 'bg-pink-500'
      }]
    }
  }, {
    type: 'summary',
    title: '知识总结',
    icon: '📝',
    content: {
      points: [{
        emoji: '🎼',
        text: '旋律是音乐的"灵魂"，由高低不同的音组成'
      }, {
        emoji: '📈',
        text: '上行旋律：音从低到高，给人向上的感觉'
      }, {
        emoji: '📉',
        text: '下行旋律：音从高到低，给人放松的感觉'
      }, {
        emoji: '➡️',
        text: '平行旋律：音高相同，给人稳定的感觉'
      }]
    }
  }];
  const currentLesson = lessonSteps[currentStep];
  const [melodyIndex, setMelodyIndex] = useState(0);
  const [createdMelody, setCreatedMelody] = useState<number[]>([]);
  const handleNextStep = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    setSelectedAnswer(null);
    setShowResult(false);
    setMelodyIndex(0);
    if (currentStep < lessonSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const renderStepContent = () => {
    switch (currentLesson.type) {
      case 'learn':
        const learnContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-442">
            <Card className="p-6 bg-purple-50 border-purple-100" data-id="element-443">
              <div className="flex items-start space-x-4" data-id="element-444">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" data-id="element-445">
                  💡
                </div>
                <div data-id="element-446">
                  <h4 className="font-bold text-purple-800 mb-2" data-id="element-447">定义</h4>
                  <p className="text-purple-700" data-id="element-448">{learnContent.definition}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white" data-id="element-449">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center" data-id="element-450">
                <Lightbulb className="mr-2 text-amber-500" size={20} data-id="element-451" />
                重要知识点
              </h4>
              <div className="space-y-3" data-id="element-452">
                {learnContent.keyPoints.map((point: string, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.2
              }} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg" data-id="element-453">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={20} data-id="element-454" />
                    <span className="text-slate-700" data-id="element-455">{point}</span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-100" data-id="element-456">
              <h4 className="font-bold text-amber-800 mb-2" data-id="element-457">🎢 生活中的例子</h4>
              <p className="text-amber-700 mb-4" data-id="element-458">{learnContent.example}</p>
              <div className="text-center text-2xl py-4 bg-white rounded-xl font-mono" data-id="element-459">
                {learnContent.visual}
              </div>
            </Card>
          </div>;
      case 'interactive':
        const interactiveContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-460">
            <Card className="p-6 bg-purple-50 border-purple-100" data-id="element-461">
              <p className="text-purple-700 font-medium text-center" data-id="element-462">
                点击下方卡片，听听不同走向的旋律
              </p>
            </Card>

            <div className="space-y-4" data-id="element-463">
              {interactiveContent.directions.map((dir: any, i: number) => <motion.button key={i} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => playMelody(dir.notes)} className={`w-full p-6 rounded-2xl border-2 ${dir.color} text-left transition-all hover:shadow-lg`} data-id="element-464">
                  <div className="flex items-center justify-between" data-id="element-465">
                    <div className="flex items-center space-x-4" data-id="element-466">
                      <span className="text-4xl" data-id="element-467">{dir.icon}</span>
                      <div data-id="element-468">
                        <h4 className="font-bold text-lg" data-id="element-469">{dir.name}</h4>
                        <p className="text-sm opacity-80" data-id="element-470">{dir.desc}</p>
                      </div>
                    </div>
                    <Play size={24} fill="currentColor" data-id="element-471" />
                  </div>
                </motion.button>)}
            </div>
          </div>;
      case 'quiz':
        const quizContent = currentLesson.content as any;
        const currentMelodyData = quizContent.melodies[melodyIndex];
        return <div className="space-y-6" data-id="element-472">
            <Card className="p-6 bg-white" data-id="element-473">
              <p className="text-slate-700 font-medium text-center mb-6" data-id="element-474">
                {quizContent.question}
              </p>

              <div className="flex justify-center mb-6" data-id="element-475">
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => playMelody(currentMelodyData.notes)} className="px-8 py-4 bg-purple-100 text-purple-700 rounded-xl font-bold hover:bg-purple-200 flex items-center" data-id="element-476">
                  <Play className="mr-2" fill="currentColor" size={20} data-id="element-477" />
                  播放旋律 #{melodyIndex + 1}
                </motion.button>
              </div>

              <div className="grid grid-cols-3 gap-4" data-id="element-478">
                {[{
                icon: <TrendingUp size={32} data-id="element-479" />,
                name: '上行',
                color: 'green'
              }, {
                icon: <TrendingDown size={32} data-id="element-480" />,
                name: '下行',
                color: 'blue'
              }, {
                icon: <Minus size={32} data-id="element-481" />,
                name: '平行',
                color: 'purple'
              }].map((option, i) => <button key={i} onClick={() => {
                if (!showResult) {
                  setSelectedAnswer(i);
                  setShowResult(true);
                  if (i === currentMelodyData.answer) {
                    setScore(s => s + 10);
                  }
                }
              }} disabled={showResult} className={`p-6 rounded-xl border-2 flex flex-col items-center transition-all ${showResult ? i === currentMelodyData.answer ? 'border-green-500 bg-green-50' : selectedAnswer === i ? 'border-red-500 bg-red-50' : 'border-slate-200 opacity-50' : `border-slate-200 hover:border-${option.color}-300 hover:bg-${option.color}-50`}`} data-id="element-482">
                    <div className={showResult && i === currentMelodyData.answer ? 'text-green-600' : `text-${option.color}-500`} data-id="element-483">
                      {option.icon}
                    </div>
                    <span className="font-bold mt-2" data-id="element-484">{option.name}</span>
                  </button>)}
              </div>
            </Card>

            {showResult && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-485">
                <Card className={`p-6 ${selectedAnswer === currentMelodyData.answer ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-486">
                  <h4 className={`font-bold mb-2 ${selectedAnswer === currentMelodyData.answer ? 'text-green-800' : 'text-amber-800'}`} data-id="element-487">
                    {selectedAnswer === currentMelodyData.answer ? '🎉 回答正确！' : '💪 继续加油！'}
                  </h4>
                  <p className={selectedAnswer === currentMelodyData.answer ? 'text-green-700' : 'text-amber-700'} data-id="element-488">
                    这是一段{currentMelodyData.name}旋律，音符
                    {currentMelodyData.answer === 0 ? '从低到高' : currentMelodyData.answer === 1 ? '从高到低' : '保持不变'}
                    。
                  </p>
                  {melodyIndex < quizContent.melodies.length - 1 && <button onClick={() => {
                setMelodyIndex(melodyIndex + 1);
                setSelectedAnswer(null);
                setShowResult(false);
              }} className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg font-bold" data-id="element-489">
                      下一题
                    </button>}
                </Card>
              </motion.div>}
          </div>;
      case 'draw':
        const drawContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-490">
            <Card className="p-6 bg-teal-50 border-teal-100" data-id="element-491">
              <p className="text-teal-700 font-medium text-center" data-id="element-492">
                {drawContent.instruction}
              </p>
            </Card>

            <button onClick={() => playMelody(drawContent.targetMelody)} className="w-full py-4 bg-teal-600 text-white rounded-xl font-bold" data-id="element-493">
              🔊 播放旋律
            </button>

            <Card className="h-64 bg-white relative overflow-hidden cursor-crosshair" ref={canvasRef} onMouseDown={e => {
            setIsDrawing(true);
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            setDrawnPath([{
              x: e.clientX - rect.left,
              y: e.clientY - rect.top
            }]);
          }} onMouseMove={e => {
            if (!isDrawing) return;
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            setDrawnPath(prev => [...prev, {
              x: e.clientX - rect.left,
              y: e.clientY - rect.top
            }]);
          }} onMouseUp={() => setIsDrawing(false)} onMouseLeave={() => setIsDrawing(false)} data-id="element-494">
              <svg className="absolute inset-0 w-full h-full" data-id="element-495">
                {drawnPath.length > 1 && <path d={`M ${drawnPath.map(p => `${p.x} ${p.y}`).join(' L ')}`} fill="none" stroke="#14B8A6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-496" />}
              </svg>
              {drawnPath.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-slate-400" data-id="element-497">
                  在这里画出旋律的走向 ✏️
                </div>}
            </Card>

            <button onClick={() => {
            setDrawnPath([]);
            setScore(s => s + 15);
          }} className="w-full py-3 border border-slate-200 rounded-xl font-medium text-slate-600" data-id="element-498">
              清除重画
            </button>
          </div>;
      case 'create':
        const createContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-499">
            <Card className="p-6 bg-pink-50 border-pink-100" data-id="element-500">
              <p className="text-pink-700 font-medium text-center" data-id="element-501">
                {createContent.instruction}
              </p>
            </Card>

            {/* Created melody display */}
            <Card className="p-4 bg-white min-h-[80px]" data-id="element-502">
              <div className="flex items-center space-x-2 flex-wrap" data-id="element-503">
                {createdMelody.length === 0 ? <span className="text-slate-400" data-id="element-504">
                    你的旋律将显示在这里...
                  </span> : createdMelody.map((freq, i) => {
                const note = createContent.notes.find((n: any) => n.freq === freq);
                return <motion.span key={i} initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className={`px-3 py-1 rounded-full text-white font-bold ${note?.color || 'bg-slate-500'}`} data-id="element-505">
                        {note?.name}
                      </motion.span>;
              })}
              </div>
            </Card>

            {/* Piano keys */}
            <div className="flex justify-center gap-1" data-id="element-506">
              {createContent.notes.map((note: any, i: number) => <motion.button key={i} whileHover={{
              scale: 1.05,
              y: -5
            }} whileTap={{
              scale: 0.95
            }} onClick={() => {
              playNote(note.freq);
              setCreatedMelody(prev => [...prev, note.freq]);
            }} className={`w-12 h-24 rounded-xl ${note.color} text-white font-bold shadow-lg flex items-end justify-center pb-2`} data-id="element-507">
                  {note.name}
                </motion.button>)}
            </div>

            <div className="flex space-x-3" data-id="element-508">
              <button onClick={() => setCreatedMelody([])} className="flex-1 py-3 border border-slate-200 rounded-xl font-medium text-slate-600" data-id="element-509">
                清除
              </button>
              <button onClick={() => {
              if (createdMelody.length > 0) {
                playMelody(createdMelody, 350);
              }
            }} disabled={createdMelody.length === 0} className="flex-1 py-3 bg-pink-500 text-white rounded-xl font-bold disabled:opacity-50" data-id="element-510">
                🎵 播放我的旋律
              </button>
            </div>
          </div>;
      case 'summary':
        const summaryContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-511">
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50" data-id="element-512">
              <div className="text-center mb-6" data-id="element-513">
                <div className="text-6xl mb-4" data-id="element-514">🎓</div>
                <h3 className="text-2xl font-bold text-slate-800" data-id="element-515">
                  恭喜完成学习！
                </h3>
                <p className="text-slate-500" data-id="element-516">你已经掌握了旋律的基础知识</p>
              </div>

              <div className="space-y-4" data-id="element-517">
                {summaryContent.points.map((point: any, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.15
              }} className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm" data-id="element-518">
                    <span className="text-2xl" data-id="element-519">{point.emoji}</span>
                    <span className="text-slate-700 font-medium" data-id="element-520">
                      {point.text}
                    </span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-200" data-id="element-521">
              <div className="flex items-center justify-between" data-id="element-522">
                <div data-id="element-523">
                  <h4 className="font-bold text-amber-800" data-id="element-524">本次学习得分</h4>
                  <p className="text-amber-600 text-sm" data-id="element-525">
                    继续努力，争取更高分！
                  </p>
                </div>
                <div className="flex items-center space-x-2" data-id="element-526">
                  <Star className="text-amber-500" fill="currentColor" data-id="element-527" />
                  <span className="text-3xl font-bold text-amber-700" data-id="element-528">
                    {score}
                  </span>
                </div>
              </div>
            </Card>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full flex flex-col bg-slate-50" data-id="element-529">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-530">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-531">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 bg-slate-50 px-4 py-2 rounded-full" data-id="element-532">
            <ArrowLeft size={20} data-id="element-533" />
            <span className="font-bold text-sm" data-id="element-534">返回</span>
          </button>

          <h1 className="text-xl font-bold text-slate-900" data-id="element-535">旋律感知</h1>

          <div className="flex items-center space-x-2 bg-amber-50 px-4 py-2 rounded-full" data-id="element-536">
            <Star className="text-amber-500" size={18} fill="currentColor" data-id="element-537" />
            <span className="font-bold text-amber-700" data-id="element-538">{score}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto px-4 pb-4" data-id="element-539">
          <div className="flex items-center space-x-2" data-id="element-540">
            {lessonSteps.map((step, i) => <div key={i} className="flex-1" data-id="element-541">
                <div className={`h-2 rounded-full ${completedSteps.includes(i) ? 'bg-purple-500' : i === currentStep ? 'bg-purple-400' : 'bg-slate-200'}`} data-id="element-542" />
              </div>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8" data-id="element-543">
        <div className="max-w-2xl mx-auto" data-id="element-544">
          {/* Step Header */}
          <div className="mb-6 text-center" data-id="element-545">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4" data-id="element-546">
              <span className="text-2xl" data-id="element-547">{currentLesson.icon}</span>
              <span className="text-sm font-medium text-slate-500" data-id="element-548">
                第 {currentStep + 1} 步 / 共 {lessonSteps.length} 步
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-549">
              {currentLesson.title}
            </h2>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait" data-id="element-550">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-551">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-slate-100 p-4" data-id="element-552">
        <div className="max-w-2xl mx-auto flex justify-between items-center" data-id="element-553">
          <button onClick={() => {
          setSelectedAnswer(null);
          setShowResult(false);
          if (currentStep > 0) setCurrentStep(currentStep - 1);
        }} disabled={currentStep === 0} className="px-6 py-2 rounded-full border border-slate-200 text-slate-600 font-medium disabled:opacity-50" data-id="element-554">
            上一步
          </button>

          <button onClick={currentStep === lessonSteps.length - 1 ? onBack : handleNextStep} className="px-8 py-2 rounded-full bg-purple-600 text-white font-bold shadow-md hover:bg-purple-700" data-id="element-555">
            {currentStep === lessonSteps.length - 1 ? '完成学习' : '下一步'}
            {currentStep < lessonSteps.length - 1 && <ChevronRight className="inline ml-1" size={18} data-id="element-556" />}
          </button>
        </div>
      </div>
    </motion.div>;
}